import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";

import {
  getPaymentAuthenticationRequired,
  isApiError,
  isTerminalPaymentAttemptError,
  transactionApi,
  updatePaymentAuthenticationForError,
} from "@bw/shared/api";
import {
  AddCardForm,
  ConnectedElementsProvider,
  PaymentIntentAuthentication,
} from "@bw/shared/components";
import {
  canonicalizeDropInPaymentOptions,
  getSavedPaymentMethodUnavailableLabel,
  reconcileSavedPaymentMethodSelection,
} from "@bw/shared/utils";
import type { StripeFrontendConfigResponse } from "@bw/shared/types/generated";

import {
  customerIdentitySuffix,
  type CustomerPicker,
  type SelectedCustomer,
} from "@/components/customer-picker";
import {
  createQuickDropinPaymentMethodSetupIntent,
  getQuickDropinPaymentMethods,
  getQuickDropinPaymentOptions,
  quickDropinCheckin,
  type QuickDropinPaymentMethod,
  type QuickDropinPaymentOptions,
  type QuickDropinCheckInRequest,
} from "@/libs/checkinApi";

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

type PaymentChoice =
  | { method: "single" }
  | { method: "package" | "membership"; subscriptionId: string };

type PaymentOptionsError = {
  message: string;
  retryable: boolean;
};

export const QUICK_DROPIN_FACILITY_REQUIREMENT =
  "Quick drop-in requires an approved, active facility.";
const QUICK_DROPIN_FACILITY_UNAVAILABLE =
  "Quick drop-in is unavailable at this facility.";

function quickDropinPaymentMethodsQueryKey(
  facilityId: string,
  playerUserId: string | null,
) {
  return [
    "check-in",
    "quick-dropin-payment-methods",
    facilityId,
    playerUserId,
  ] as const;
}

type PendingCardReconciliation = {
  facilityId: string;
  playerUserId: string;
  paymentMethodId: string;
  generation: number;
};

type CardSetupTarget = {
  facilityId: string;
  playerUserId: string;
  generation: number;
};

function paymentChoiceFromRequest(
  body: Omit<QuickDropinCheckInRequest, "idempotency_key">,
): PaymentChoice {
  if (body.payment_method === "single") return { method: "single" };
  return {
    method: body.payment_method,
    subscriptionId: body.subscription_id ?? "",
  };
}

function paymentChoiceValue(choice: PaymentChoice | null): string {
  if (!choice) return "";
  return choice.method === "single"
    ? "single"
    : `${choice.method}:${choice.subscriptionId}`;
}

function formatDropinPrice(
  value: QuickDropinPaymentOptions["single_price"],
  currency: string,
): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "Single drop-in";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);
}

type QuickDropinAttemptBody = QuickDropinCheckInRequest extends infer Request
  ? Request extends QuickDropinCheckInRequest
    ? Omit<Request, "idempotency_key">
    : never
  : never;

type QuickDropinAttempt = {
  operatorUid: string;
  customerLabel: string;
  idempotencyKey: string;
  body: QuickDropinAttemptBody;
  operationToken?: string;
};

const QUICK_DROPIN_ATTEMPT_STORAGE_PREFIX = "bw:business-quick-dropin-attempt:";

function quickDropinAttemptStorageKey(facilityId: string): string {
  return `${QUICK_DROPIN_ATTEMPT_STORAGE_PREFIX}${facilityId}`;
}

function parseQuickDropinAttempt(raw: string): QuickDropinAttempt | null {
  try {
    const parsed = JSON.parse(raw) as {
      operatorUid?: unknown;
      customerLabel?: unknown;
      idempotencyKey?: unknown;
      operationToken?: unknown;
      body?: Record<string, unknown>;
    };
    const method = parsed.body?.payment_method;
    const playerUserId = parsed.body?.player_user_id;
    if (
      typeof parsed.operatorUid !== "string" ||
      parsed.operatorUid.length === 0 ||
      typeof parsed.customerLabel !== "string" ||
      parsed.customerLabel.trim().length === 0 ||
      typeof parsed.idempotencyKey !== "string" ||
      (parsed.operationToken !== undefined &&
        (typeof parsed.operationToken !== "string" ||
          parsed.operationToken.length === 0)) ||
      typeof playerUserId !== "string" ||
      playerUserId.length === 0 ||
      (method !== "single" && method !== "package" && method !== "membership")
    ) {
      return null;
    }

    const stripePaymentMethodId = parsed.body?.stripe_payment_method_id;
    const subscriptionId = parsed.body?.subscription_id;
    const priceQuoteVersion = parsed.body?.price_quote_version;
    const cancellationPolicyCode = parsed.body?.cancellation_policy_code;
    const cancellationPolicyId = parsed.body?.cancellation_policy_id;
    const cancellationPolicyVersion = parsed.body?.cancellation_policy_version;
    const checkoutActivationMode = parsed.body?.checkout_activation_mode;
    if (
      (stripePaymentMethodId !== undefined &&
        stripePaymentMethodId !== null &&
        typeof stripePaymentMethodId !== "string") ||
      (subscriptionId !== undefined &&
        subscriptionId !== null &&
        typeof subscriptionId !== "string") ||
      (priceQuoteVersion !== undefined &&
        priceQuoteVersion !== null &&
        typeof priceQuoteVersion !== "string") ||
      cancellationPolicyCode !== "open_play_cancellation" ||
      typeof cancellationPolicyId !== "string" ||
      cancellationPolicyId.trim().length === 0 ||
      typeof cancellationPolicyVersion !== "number" ||
      !Number.isInteger(cancellationPolicyVersion) ||
      cancellationPolicyVersion < 1 ||
      (checkoutActivationMode !== "immediate" &&
        checkoutActivationMode !== "staff_validation")
    ) {
      return null;
    }
    const cancellationPolicy = {
      cancellation_policy_code: cancellationPolicyCode,
      cancellation_policy_id: cancellationPolicyId,
      cancellation_policy_version: cancellationPolicyVersion,
      checkout_activation_mode: checkoutActivationMode,
    } as const;
    // Rebuild the request body so persisted data cannot inject facility scope.
    let body: QuickDropinAttemptBody;
    if (method === "single") {
      if (
        typeof priceQuoteVersion !== "string" ||
        !/^[0-9a-f]{64}$/.test(priceQuoteVersion)
      ) {
        return null;
      }
      body = {
        ...cancellationPolicy,
        player_user_id: playerUserId,
        payment_method: "single",
        price_quote_version: priceQuoteVersion,
        ...(typeof stripePaymentMethodId === "string"
          ? { stripe_payment_method_id: stripePaymentMethodId }
          : {}),
      };
    } else {
      if (typeof subscriptionId !== "string") return null;
      body = {
        ...cancellationPolicy,
        player_user_id: playerUserId,
        payment_method: method,
        subscription_id: subscriptionId,
      };
    }
    return {
      operatorUid: parsed.operatorUid,
      customerLabel: parsed.customerLabel.trim(),
      idempotencyKey: parsed.idempotencyKey,
      body,
      ...(typeof parsed.operationToken === "string"
        ? { operationToken: parsed.operationToken }
        : {}),
    };
  } catch {
    return null;
  }
}

function removeStoredQuickDropinAttempt(
  storageKey: string,
  expectedRaw: string,
): void {
  try {
    if (window.sessionStorage.getItem(storageKey) === expectedRaw) {
      window.sessionStorage.removeItem(storageKey);
    }
  } catch {
    // Storage can be unavailable in hardened browser contexts.
  }
}

export function reconcileQuickDropinAttemptStorage(
  operatorUid: string | null,
): void {
  const storedValues: Array<{ key: string; raw: string }> = [];
  try {
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (!key?.startsWith(QUICK_DROPIN_ATTEMPT_STORAGE_PREFIX)) continue;
      const raw = window.sessionStorage.getItem(key);
      if (raw !== null) storedValues.push({ key, raw });
    }
  } catch {
    return;
  }

  for (const { key, raw } of storedValues) {
    const storedAttempt = parseQuickDropinAttempt(raw);
    if (
      operatorUid === null ||
      storedAttempt === null ||
      storedAttempt.operatorUid !== operatorUid
    ) {
      removeStoredQuickDropinAttempt(key, raw);
    }
  }
}

function loadQuickDropinAttempt(
  facilityId: string,
  operatorUid: string,
): QuickDropinAttempt | null {
  const storageKey = quickDropinAttemptStorageKey(facilityId);
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = parseQuickDropinAttempt(raw);
    if (parsed === null || parsed.operatorUid !== operatorUid) {
      removeStoredQuickDropinAttempt(storageKey, raw);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearStoredQuickDropinAttempt(
  facilityId: string,
  expectedAttempt: QuickDropinAttempt,
): void {
  const storageKey = quickDropinAttemptStorageKey(facilityId);
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return;
    const storedAttempt = parseQuickDropinAttempt(raw);
    if (
      storedAttempt?.operatorUid === expectedAttempt.operatorUid &&
      storedAttempt.idempotencyKey === expectedAttempt.idempotencyKey &&
      storedAttempt.operationToken === expectedAttempt.operationToken
    ) {
      removeStoredQuickDropinAttempt(storageKey, raw);
    }
  } catch {
    // The in-memory attempt can still be cleared when storage is unavailable.
  }
}

function isSameQuickDropinAttempt(
  left: QuickDropinAttempt | null,
  right: QuickDropinAttempt,
): boolean {
  return (
    left?.operatorUid === right.operatorUid &&
    left.idempotencyKey === right.idempotencyKey &&
    left.operationToken === right.operationToken
  );
}

function QuickDropinCardEntry({
  autoFocus,
  config,
  createSetupIntent,
  error,
  loading,
  onBusyChange,
  onCardAdded,
  onRetry,
}: {
  autoFocus: boolean;
  config: StripeFrontendConfigResponse | null;
  createSetupIntent: (idempotencyKey: string) => Promise<string>;
  error: string | null;
  loading: boolean;
  onBusyChange: (busy: boolean) => void;
  onCardAdded: (
    paymentMethodId: string,
    idempotencyKey: string,
  ) => void | Promise<void>;
  onRetry: () => void;
}) {
  if (config) {
    return (
      <ConnectedElementsProvider
        config={config}
        unavailableFallback={
          <Alert severity="error" role="alert">
            This facility can&apos;t take card payments yet.
          </Alert>
        }
      >
        <AddCardForm
          publishableKey={config.publishable_key}
          autoFocus={autoFocus}
          createSetupIntent={createSetupIntent}
          onBusyChange={onBusyChange}
          onCardAdded={onCardAdded}
          render={(model) => (
            <Stack ref={model.fieldsRef} spacing={1.5}>
              <Typography variant="subtitle2">Add a new card</Typography>
              <CardElementField
                id={model.fields.number.id}
                label={model.fields.number.label}
                focused={model.focusedField === "number"}
                disabled={model.fieldsLocked}
              >
                {model.fields.number.element}
              </CardElementField>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <CardElementField
                  id={model.fields.expiry.id}
                  label={model.fields.expiry.label}
                  focused={model.focusedField === "expiry"}
                  disabled={model.fieldsLocked}
                >
                  {model.fields.expiry.element}
                </CardElementField>
                <CardElementField
                  id={model.fields.cvc.id}
                  label={model.fields.cvc.label}
                  focused={model.focusedField === "cvc"}
                  disabled={model.fieldsLocked}
                >
                  {model.fields.cvc.element}
                </CardElementField>
              </Stack>
              <TextField
                id={model.postal.id}
                label="ZIP or postal code"
                value={model.postal.value}
                disabled={model.postal.disabled}
                onChange={(event) => model.postal.onChange(event.target.value)}
                fullWidth
              />
              {model.error ? (
                <Alert severity="error">{model.error}</Alert>
              ) : null}
              {model.saveDisabledReason ? (
                <FormHelperText id={model.saveDisabledReasonId}>
                  {model.saveDisabledReason}
                </FormHelperText>
              ) : null}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="button"
                  size="small"
                  variant="contained"
                  disabled={model.submit.disabled}
                  aria-describedby={
                    model.saveDisabledReason
                      ? model.saveDisabledReasonId
                      : undefined
                  }
                  onClick={model.submit.onClick}
                >
                  {model.submit.label}
                </Button>
              </Box>
            </Stack>
          )}
        />
      </ConnectedElementsProvider>
    );
  }

  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary" role="status">
        Loading secure card entry...
      </Typography>
    );
  }

  return (
    <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Button onClick={onRetry} size="small" type="button" variant="outlined">
        Retry secure card entry
      </Button>
    </Stack>
  );
}

function CardElementField({
  id,
  label,
  focused,
  disabled,
  children,
}: {
  id: string;
  label: string;
  focused: boolean;
  disabled: boolean;
  children: ReactNode;
}) {
  return (
    <Box component="label" htmlFor={id} sx={{ display: "block", flex: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box
        sx={{
          mt: 0.5,
          minHeight: 44,
          display: "flex",
          alignItems: "center",
          px: 1.5,
          border: 1,
          borderColor: focused ? "primary.main" : "divider",
          borderRadius: 1,
          opacity: disabled ? 0.6 : 1,
          "& > *": { width: "100%" },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export function QuickDropinDialog({
  facilityId,
  facilityName,
  operatorUid,
  CustomerPickerComponent,
}: {
  facilityId: string;
  facilityName: string;
  operatorUid: string | null;
  CustomerPickerComponent: typeof CustomerPicker;
}) {
  const [paymentMethodsQueryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: false } },
      }),
  );
  const [open, setOpen] = useState(false);
  const [attempt, setAttempt] = useState<QuickDropinAttempt | null>(() =>
    operatorUid ? loadQuickDropinAttempt(facilityId, operatorUid) : null,
  );
  const [selectedCustomer, setSelectedCustomer] =
    useState<SelectedCustomer | null>(
      attempt
        ? {
            userId: attempt.body.player_user_id,
            displayName: attempt.customerLabel,
            identitySuffix: customerIdentitySuffix(attempt.body.player_user_id),
          }
        : null,
    );
  const [paymentOptions, setPaymentOptions] =
    useState<QuickDropinPaymentOptions | null>(null);
  const [paymentMethodsRetrying, setPaymentMethodsRetrying] = useState(false);
  const [paymentMethodsRetryAnnouncement, setPaymentMethodsRetryAnnouncement] =
    useState("");
  const [focusCardEntryAfterRetry, setFocusCardEntryAfterRetry] =
    useState(false);
  const [paymentChoice, setPaymentChoice] = useState<PaymentChoice | null>(
    attempt ? paymentChoiceFromRequest(attempt.body) : null,
  );
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentOptionsReload, setPaymentOptionsReload] = useState(0);
  const [paymentError, setPaymentError] = useState<PaymentOptionsError | null>(
    null,
  );
  const [quickDropinUnavailable, setQuickDropinUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [frontendConfig, setFrontendConfig] =
    useState<StripeFrontendConfigResponse | null>(null);
  const [cardConfigLoading, setCardConfigLoading] = useState(false);
  const [cardConfigReload, setCardConfigReload] = useState(0);
  const [cardSetupBusy, setCardSetupBusy] = useState(false);
  const [cardSetupError, setCardSetupError] = useState<string | null>(null);
  const [pendingCard, setPendingCard] =
    useState<PendingCardReconciliation | null>(null);
  const [paymentAuthentication, setPaymentAuthentication] = useState<{
    clientSecret: string;
    attempt: QuickDropinAttempt;
    operationId: number;
  } | null>(null);
  const [postScaWaiverRefunded, setPostScaWaiverRefunded] = useState(false);
  const operationGeneration = useRef(0);
  const cardReconciliationGeneration = useRef(0);
  const cardSetupTargets = useRef(new Map<string, CardSetupTarget>());
  const selectedCustomerIdRef = useRef(selectedCustomer?.userId);
  const selectedFacilityIdRef = useRef(facilityId);
  const paymentMethodsRetryingRef = useRef(false);
  const paymentMethodsRetryGeneration = useRef(0);
  const focusSavedCardAfterRetryRef = useRef(false);
  const paymentSelectionContextRef = useRef(
    `${facilityId}:${selectedCustomer?.userId ?? ""}`,
  );
  const initializedPaymentSelectionContextRef = useRef<string | null>(
    attempt ? paymentSelectionContextRef.current : null,
  );
  selectedCustomerIdRef.current = selectedCustomer?.userId;
  selectedFacilityIdRef.current = facilityId;

  const paymentMethodsPlayerId = selectedCustomer?.userId ?? null;
  const paymentMethodsEnabled =
    paymentMethodsPlayerId !== null &&
    attempt === null &&
    paymentOptions?.single_visit_available === true &&
    paymentOptions.single_requires_payment_method;
  const paymentMethodsQueryKey = quickDropinPaymentMethodsQueryKey(
    facilityId,
    paymentMethodsPlayerId,
  );
  const paymentMethodsQuery = useQuery<QuickDropinPaymentMethod[]>(
    {
      queryKey: paymentMethodsQueryKey,
      queryFn: () =>
        getQuickDropinPaymentMethods(facilityId, paymentMethodsPlayerId!),
      enabled: paymentMethodsEnabled,
      retry: false,
      staleTime: 0,
    },
    paymentMethodsQueryClient,
  );
  const paymentMethods = paymentMethodsQuery.data ?? [];
  const paymentMethodsUnavailable = paymentMethodsQuery.isError;
  const paymentMethodsLoading =
    paymentMethodsQuery.isPending &&
    paymentMethodsQuery.data === undefined &&
    !paymentMethodsUnavailable;
  const paymentMethodsSettled =
    paymentMethodsEnabled &&
    paymentMethodsQuery.data !== undefined &&
    !paymentMethodsQuery.isFetching &&
    !paymentMethodsQuery.isError;
  const chargeReadyPaymentMethodId =
    paymentMethodsSettled && paymentMethodsQuery.data !== undefined
      ? reconcileSavedPaymentMethodSelection(
          paymentMethodsQuery.data,
          selectedPaymentMethodId || null,
          false,
        )
      : null;

  useEffect(() => {
    paymentMethodsQueryClient.mount();
    return () => {
      operationGeneration.current += 1;
      cardReconciliationGeneration.current += 1;
      paymentMethodsRetryGeneration.current += 1;
      cardSetupTargets.current.clear();
      paymentMethodsQueryClient.unmount();
      paymentMethodsQueryClient.clear();
    };
  }, [paymentMethodsQueryClient]);

  useEffect(() => {
    const context = `${facilityId}:${selectedCustomer?.userId ?? ""}`;
    if (paymentSelectionContextRef.current === context) return;
    paymentSelectionContextRef.current = context;
    initializedPaymentSelectionContextRef.current = null;
    paymentMethodsRetryGeneration.current += 1;
    paymentMethodsRetryingRef.current = false;
    focusSavedCardAfterRetryRef.current = false;
    setPaymentMethodsRetrying(false);
    setPaymentMethodsRetryAnnouncement("");
    setFocusCardEntryAfterRetry(false);
    setSelectedPaymentMethodId("");
  }, [facilityId, selectedCustomer?.userId]);

  useEffect(() => {
    const methods = paymentMethodsQuery.data;
    const context = `${facilityId}:${selectedCustomer?.userId ?? ""}`;
    if (attempt !== null) {
      initializedPaymentSelectionContextRef.current = context;
      return;
    }
    if (
      !paymentMethodsEnabled ||
      methods === undefined ||
      paymentMethodsQuery.isFetching ||
      paymentMethodsQuery.isError
    ) {
      return;
    }
    const initializeProviderDefault =
      initializedPaymentSelectionContextRef.current !== context;
    initializedPaymentSelectionContextRef.current = context;
    setSelectedPaymentMethodId(
      (current) =>
        reconcileSavedPaymentMethodSelection(
          methods,
          current || null,
          initializeProviderDefault,
        ) ?? "",
    );
  }, [
    attempt,
    facilityId,
    paymentMethodsEnabled,
    paymentMethodsQuery.data,
    paymentMethodsQuery.isError,
    paymentMethodsQuery.isFetching,
    selectedCustomer?.userId,
  ]);

  useEffect(() => {
    if (
      paymentMethodsRetrying ||
      !focusSavedCardAfterRetryRef.current ||
      paymentMethods.length === 0
    ) {
      return;
    }
    focusSavedCardAfterRetryRef.current = false;
    document.getElementById("saved-card")?.focus();
  }, [paymentMethods, paymentMethodsRetrying]);

  const retryPaymentMethods = async () => {
    if (paymentMethodsRetryingRef.current) return;
    const generation = ++paymentMethodsRetryGeneration.current;
    const targetFacilityId = facilityId;
    const targetPlayerUserId = paymentMethodsPlayerId;
    const targetIsCurrent = () =>
      paymentMethodsRetryGeneration.current === generation &&
      selectedFacilityIdRef.current === targetFacilityId &&
      selectedCustomerIdRef.current === targetPlayerUserId;
    paymentMethodsRetryingRef.current = true;
    focusSavedCardAfterRetryRef.current = true;
    setPaymentMethodsRetrying(true);
    setPaymentMethodsRetryAnnouncement("Retrying saved cards.");
    setFocusCardEntryAfterRetry(false);
    try {
      const result = await paymentMethodsQuery.refetch();
      if (!targetIsCurrent()) return;
      if (result.isError) {
        focusSavedCardAfterRetryRef.current = false;
        setFocusCardEntryAfterRetry(false);
        setPaymentMethodsRetryAnnouncement(
          "Saved cards are still unavailable. Retry again.",
        );
      } else if ((result.data?.length ?? 0) === 0) {
        focusSavedCardAfterRetryRef.current = false;
        setFocusCardEntryAfterRetry(true);
        setPaymentMethodsRetryAnnouncement(
          "No saved cards found. Add a card to continue.",
        );
      } else {
        setFocusCardEntryAfterRetry(false);
        setPaymentMethodsRetryAnnouncement("Saved cards loaded.");
      }
    } catch {
      if (!targetIsCurrent()) return;
      focusSavedCardAfterRetryRef.current = false;
      setFocusCardEntryAfterRetry(false);
      setPaymentMethodsRetryAnnouncement(
        "Saved cards are still unavailable. Retry again.",
      );
    } finally {
      if (targetIsCurrent()) {
        paymentMethodsRetryingRef.current = false;
        setPaymentMethodsRetrying(false);
      }
    }
  };

  const reset = () => {
    operationGeneration.current += 1;
    cardReconciliationGeneration.current += 1;
    paymentMethodsRetryGeneration.current += 1;
    cardSetupTargets.current.clear();
    setBusy(false);
    setCardSetupBusy(false);
    setCardSetupError(null);
    setPendingCard(null);
    paymentMethodsRetryingRef.current = false;
    focusSavedCardAfterRetryRef.current = false;
    setPaymentMethodsRetrying(false);
    setPaymentMethodsRetryAnnouncement("");
    setFocusCardEntryAfterRetry(false);
    if (attempt) {
      setSelectedCustomer({
        userId: attempt.body.player_user_id,
        displayName: attempt.customerLabel,
        identitySuffix: customerIdentitySuffix(attempt.body.player_user_id),
      });
      setPaymentChoice(paymentChoiceFromRequest(attempt.body));
      setSelectedPaymentMethodId("");
    } else {
      setSelectedCustomer(null);
      setPaymentOptions(null);
      setPaymentChoice(null);
      setSelectedPaymentMethodId("");
    }
    setPaymentError(null);
    setQuickDropinUnavailable(false);
    setError(null);
    setSuccess(null);
    setPaymentAuthentication(null);
    setPostScaWaiverRefunded(false);
  };

  useEffect(() => {
    const playerUserId = selectedCustomer?.userId;
    if (!playerUserId || attempt || quickDropinUnavailable) return;

    let cancelled = false;
    setPaymentLoading(true);
    setPaymentError(null);
    setPaymentOptions(null);
    if (!postScaWaiverRefunded) {
      setPaymentChoice(null);
    }
    setCardSetupError(null);
    if (!postScaWaiverRefunded) {
      setPendingCard(null);
    }

    getQuickDropinPaymentOptions(facilityId, playerUserId)
      .then((options) => {
        if (cancelled) return;
        const canonicalOptions = canonicalizeDropInPaymentOptions(options);
        setPaymentOptions(canonicalOptions);
        if (postScaWaiverRefunded) {
          setPaymentChoice(
            canonicalOptions.single_visit_available &&
              canonicalOptions.single_price != null
              ? { method: "single" }
              : null,
          );
          return;
        }
        const membership = canonicalOptions.active_memberships?.[0];
        const packageOption = canonicalOptions.active_packages?.[0];
        if (membership) {
          setPaymentChoice({
            method: "membership",
            subscriptionId: membership.subscription_id,
          });
        } else if (packageOption) {
          setPaymentChoice({
            method: "package",
            subscriptionId: packageOption.subscription_id,
          });
        } else if (
          canonicalOptions.single_visit_available &&
          canonicalOptions.single_price != null
        ) {
          setPaymentChoice({ method: "single" });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const facilityUnavailable =
            isApiError(err) && err.code === "NO_ACTIVE_DROP_IN";
          setQuickDropinUnavailable(facilityUnavailable);
          setPaymentError({
            message: facilityUnavailable
              ? QUICK_DROPIN_FACILITY_UNAVAILABLE
              : "Failed to load payment options.",
            retryable: !facilityUnavailable,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setPaymentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    attempt,
    facilityId,
    paymentOptionsReload,
    postScaWaiverRefunded,
    quickDropinUnavailable,
    selectedCustomer?.userId,
  ]);

  const needsNewCardEntry =
    paymentOptions !== null &&
    !attempt &&
    paymentChoice?.method === "single" &&
    paymentOptions.single_requires_payment_method &&
    paymentMethodsQuery.data !== undefined &&
    (paymentMethods.length === 0 ||
      paymentMethods.some((method) => !method.is_usable)) &&
    pendingCard === null;

  useEffect(() => {
    if (!needsNewCardEntry || frontendConfig) return;

    let cancelled = false;
    setCardConfigLoading(true);
    setCardSetupError(null);
    transactionApi
      .getFrontendConfig(facilityId)
      .then((config) => {
        if (!cancelled) setFrontendConfig(config);
      })
      .catch((err) => {
        if (!cancelled) {
          setCardSetupError(
            errorMessage(
              err,
              "Secure card entry is unavailable. Please try again.",
            ),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setCardConfigLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cardConfigReload, facilityId, frontendConfig, needsNewCardEntry]);

  const reconcileSavedCard = async (target: PendingCardReconciliation) => {
    const targetIsCurrent = () =>
      cardReconciliationGeneration.current === target.generation &&
      facilityId === target.facilityId &&
      selectedCustomerIdRef.current === target.playerUserId;
    if (!targetIsCurrent()) return;
    setCardSetupError(null);
    try {
      const methods = await getQuickDropinPaymentMethods(
        target.facilityId,
        target.playerUserId,
      );
      if (!targetIsCurrent()) return;
      const savedMethod = methods.find(
        (method) => method.payment_method_id === target.paymentMethodId,
      );
      paymentMethodsQueryClient.setQueryData(
        quickDropinPaymentMethodsQueryKey(
          target.facilityId,
          target.playerUserId,
        ),
        methods,
      );
      if (!savedMethod || !savedMethod.is_usable) {
        const unavailableLabel = getSavedPaymentMethodUnavailableLabel(
          savedMethod?.unavailable_reason ?? null,
        );
        setCardSetupError(
          unavailableLabel
            ? `${unavailableLabel}. Add a different card or retry card refresh.`
            : "The card was saved, but it is not available yet. Retry card refresh.",
        );
        return;
      }
      setSelectedPaymentMethodId(savedMethod.payment_method_id);
      setPendingCard(null);
    } catch (err) {
      if (!targetIsCurrent()) return;
      setCardSetupError(
        errorMessage(
          err,
          "The card was saved, but the card list could not refresh. Retry card refresh.",
        ),
      );
    }
  };

  const handleCardAdded = async (
    paymentMethodId: string,
    idempotencyKey: string,
  ) => {
    const setupTarget = cardSetupTargets.current.get(idempotencyKey);
    cardSetupTargets.current.delete(idempotencyKey);
    if (
      !setupTarget ||
      cardReconciliationGeneration.current !== setupTarget.generation ||
      facilityId !== setupTarget.facilityId ||
      selectedCustomerIdRef.current !== setupTarget.playerUserId
    ) {
      return;
    }
    const target = { ...setupTarget, paymentMethodId };
    setPendingCard(target);
    try {
      await reconcileSavedCard(target);
    } finally {
      if (
        cardReconciliationGeneration.current === target.generation &&
        selectedCustomerIdRef.current === target.playerUserId
      ) {
        setCardSetupBusy(false);
      }
    }
  };

  const retryPendingCardRefresh = async () => {
    if (!pendingCard) return;
    setCardSetupBusy(true);
    try {
      await reconcileSavedCard(pendingCard);
    } finally {
      if (
        cardReconciliationGeneration.current === pendingCard.generation &&
        selectedCustomerIdRef.current === pendingCard.playerUserId
      ) {
        setCardSetupBusy(false);
      }
    }
  };

  const claimAttemptOperation = (
    nextAttempt: QuickDropinAttempt,
  ): QuickDropinAttempt => {
    const claimedAttempt = {
      ...nextAttempt,
      operationToken: globalThis.crypto.randomUUID(),
    };
    try {
      window.sessionStorage.setItem(
        quickDropinAttemptStorageKey(facilityId),
        JSON.stringify(claimedAttempt),
      );
    } catch {
      // Keep the retry stable in memory when storage is unavailable.
    }
    setAttempt(claimedAttempt);
    return claimedAttempt;
  };

  const clearAttempt = (expectedAttempt: QuickDropinAttempt) => {
    clearStoredQuickDropinAttempt(facilityId, expectedAttempt);
    setAttempt((current) =>
      isSameQuickDropinAttempt(current, expectedAttempt) ? null : current,
    );
  };

  const disarmUnavailableCardPayment = (activeAttempt: QuickDropinAttempt) => {
    clearAttempt(activeAttempt);
    cardReconciliationGeneration.current += 1;
    paymentMethodsRetryGeneration.current += 1;
    cardSetupTargets.current.clear();
    setPaymentAuthentication(null);
    setFrontendConfig(null);
    paymentMethodsRetryingRef.current = false;
    focusSavedCardAfterRetryRef.current = false;
    setPaymentMethodsRetrying(false);
    setPaymentMethodsRetryAnnouncement("");
    setFocusCardEntryAfterRetry(false);
    setSelectedPaymentMethodId("");
    setPendingCard(null);
    setCardSetupError(null);
    setPaymentChoice(null);
    setPaymentOptions(null);
    setPaymentOptionsReload((value) => value + 1);
  };

  const disarmUnavailableFacility = (activeAttempt: QuickDropinAttempt) => {
    clearAttempt(activeAttempt);
    cardReconciliationGeneration.current += 1;
    paymentMethodsRetryGeneration.current += 1;
    cardSetupTargets.current.clear();
    setPaymentAuthentication(null);
    setFrontendConfig(null);
    paymentMethodsRetryingRef.current = false;
    focusSavedCardAfterRetryRef.current = false;
    setPaymentMethodsRetrying(false);
    setPaymentMethodsRetryAnnouncement("");
    setFocusCardEntryAfterRetry(false);
    setSelectedPaymentMethodId("");
    setPendingCard(null);
    setCardSetupError(null);
    setPaymentChoice(null);
    setPaymentOptions(null);
    setPaymentError({
      message: QUICK_DROPIN_FACILITY_UNAVAILABLE,
      retryable: false,
    });
    setQuickDropinUnavailable(true);
    setPostScaWaiverRefunded(false);
    setError(null);
  };

  const buildBody = (): QuickDropinAttempt["body"] | null => {
    if (!selectedCustomer || !paymentOptions || !paymentChoice) return null;
    if (cardSetupBusy || pendingCard) return null;
    if (!paymentOptions.active_now || paymentOptions.has_active_session)
      return null;
    const cancellationPolicy = {
      cancellation_policy_code: paymentOptions.checkout_terms.policy_code,
      cancellation_policy_id: paymentOptions.checkout_terms.policy_id,
      cancellation_policy_version: paymentOptions.checkout_terms.policy_version,
      checkout_activation_mode:
        paymentOptions.checkout_terms.checkout_activation_mode,
    } as const;

    if (paymentChoice.method === "single") {
      if (!paymentOptions.single_visit_available) return null;
      if (!paymentOptions.price_quote_version) return null;
      if (
        paymentOptions.single_requires_payment_method &&
        !chargeReadyPaymentMethodId
      ) {
        return null;
      }
      return {
        ...cancellationPolicy,
        player_user_id: selectedCustomer.userId,
        payment_method: "single",
        price_quote_version: paymentOptions.price_quote_version,
        ...(chargeReadyPaymentMethodId
          ? { stripe_payment_method_id: chargeReadyPaymentMethodId }
          : {}),
      };
    }

    if (!paymentChoice.subscriptionId) return null;
    return {
      ...cancellationPolicy,
      player_user_id: selectedCustomer.userId,
      payment_method: paymentChoice.method,
      subscription_id: paymentChoice.subscriptionId,
    };
  };

  const requestBody = attempt?.body ?? buildBody();

  const applySuccessfulCheckIn = (
    session: Awaited<ReturnType<typeof quickDropinCheckin>>,
    activeAttempt: QuickDropinAttempt,
    operationId: number,
    authenticatedClientSecret?: string,
  ) => {
    if (operationGeneration.current !== operationId) return;
    clearAttempt(activeAttempt);
    setPaymentAuthentication((current) =>
      authenticatedClientSecret === undefined ||
      current?.clientSecret === authenticatedClientSecret
        ? null
        : current,
    );
    setSuccess(
      `${session.display_name?.trim() || activeAttempt.customerLabel} checked in`,
    );
    setSelectedCustomer(null);
    paymentMethodsRetryGeneration.current += 1;
    setPaymentOptions(null);
    paymentMethodsRetryingRef.current = false;
    focusSavedCardAfterRetryRef.current = false;
    setPaymentMethodsRetrying(false);
    setPaymentMethodsRetryAnnouncement("");
    setFocusCardEntryAfterRetry(false);
    setPaymentChoice(null);
    setSelectedPaymentMethodId("");
    setPendingCard(null);
    setCardSetupError(null);
    setPostScaWaiverRefunded(false);
  };

  const handleCheckIn = async () => {
    if (
      busy ||
      cardSetupBusy ||
      pendingCard !== null ||
      !operatorUid ||
      !requestBody
    ) {
      return;
    }
    const operationId = ++operationGeneration.current;
    setBusy(true);
    setError(null);
    setSuccess(null);
    setPostScaWaiverRefunded(false);
    const candidateAttempt = attempt ?? {
      operatorUid,
      customerLabel: selectedCustomer?.displayName ?? "Customer",
      idempotencyKey: globalThis.crypto.randomUUID(),
      body: requestBody,
    };
    const activeAttempt = claimAttemptOperation(candidateAttempt);
    try {
      const session = await quickDropinCheckin(facilityId, {
        ...activeAttempt.body,
        idempotency_key: activeAttempt.idempotencyKey,
      });
      applySuccessfulCheckIn(session, activeAttempt, operationId);
    } catch (err) {
      if (operationGeneration.current !== operationId) return;
      const authentication = getPaymentAuthenticationRequired(err);
      if (authentication) {
        try {
          const config =
            frontendConfig ??
            (await transactionApi.getFrontendConfig(facilityId));
          if (operationGeneration.current !== operationId) return;
          setFrontendConfig(config);
          setPaymentAuthentication({
            clientSecret: authentication.clientSecret,
            attempt: activeAttempt,
            operationId,
          });
        } catch (configError) {
          if (operationGeneration.current !== operationId) return;
          setError(
            errorMessage(
              configError,
              "Payment verification is unavailable. Please try again.",
            ),
          );
        }
      } else {
        if (isApiError(err) && err.code === "NO_ACTIVE_DROP_IN") {
          disarmUnavailableFacility(activeAttempt);
        } else if (isApiError(err) && err.code === "DROPIN_PRICE_CHANGED") {
          clearAttempt(activeAttempt);
          setPaymentOptionsReload((value) => value + 1);
          setError(
            "The drop-in price changed. Review the latest price and try again.",
          );
        } else if (isApiError(err) && err.code === "DROPIN_POLICY_CHANGED") {
          clearAttempt(activeAttempt);
          setPaymentOptionsReload((value) => value + 1);
          setError(
            "The cancellation terms changed. Review the latest terms and try again.",
          );
        } else if (
          isApiError(err) &&
          err.code === "FACILITY_PAYMENTS_UNAVAILABLE"
        ) {
          disarmUnavailableCardPayment(activeAttempt);
          setError(
            "Card payments were disabled while this check-in was in progress.",
          );
        } else if (isApiError(err) && err.status === 428) {
          setError(
            "This player has been notified to accept the current waiver on their own device. Retry this check-in after they finish; the customer and payment stay selected.",
          );
        } else {
          if (isTerminalPaymentAttemptError(err)) clearAttempt(activeAttempt);
          setError(errorMessage(err, "Failed to check in walk-in."));
        }
      }
    } finally {
      if (operationGeneration.current === operationId) setBusy(false);
    }
  };

  const finishPaymentAuthentication = async (
    authenticatedClientSecret: string,
  ) => {
    const authentication = paymentAuthentication;
    if (
      !authentication ||
      authentication.clientSecret !== authenticatedClientSecret ||
      operationGeneration.current !== authentication.operationId
    ) {
      return;
    }
    const operationId = ++operationGeneration.current;
    setError(null);
    const activeAttempt = claimAttemptOperation(authentication.attempt);
    setPaymentAuthentication((current) =>
      current?.clientSecret === authenticatedClientSecret &&
      current.operationId === authentication.operationId
        ? { ...current, attempt: activeAttempt, operationId }
        : current,
    );
    try {
      const session = await quickDropinCheckin(facilityId, {
        ...activeAttempt.body,
        idempotency_key: activeAttempt.idempotencyKey,
      });
      applySuccessfulCheckIn(
        session,
        activeAttempt,
        operationId,
        authenticatedClientSecret,
      );
    } catch (err) {
      if (operationGeneration.current !== operationId) return;
      if (isApiError(err) && err.status === 428) {
        clearAttempt(activeAttempt);
        setPaymentAuthentication((current) =>
          current?.clientSecret === authenticatedClientSecret ? null : current,
        );
        setPostScaWaiverRefunded(true);
        setError(
          "Payment was refunded because the facility waiver changed. The player has been notified to accept the current waiver. After they accept it, start a new payment; the customer and card stay selected.",
        );
      } else {
        if (isApiError(err) && err.code === "NO_ACTIVE_DROP_IN") {
          disarmUnavailableFacility(activeAttempt);
        } else {
          setPaymentAuthentication((current) =>
            updatePaymentAuthenticationForError(
              current,
              authenticatedClientSecret,
              err,
            ),
          );
          if (isApiError(err) && err.code === "DROPIN_PRICE_CHANGED") {
            clearAttempt(activeAttempt);
            setPaymentOptionsReload((value) => value + 1);
            setError(
              "The drop-in price changed. Review the latest price and try again.",
            );
          } else if (isApiError(err) && err.code === "DROPIN_POLICY_CHANGED") {
            clearAttempt(activeAttempt);
            setPaymentOptionsReload((value) => value + 1);
            setError(
              "The cancellation terms changed. Review the latest terms and try again.",
            );
          } else if (
            isApiError(err) &&
            err.code === "FACILITY_PAYMENTS_UNAVAILABLE"
          ) {
            disarmUnavailableCardPayment(activeAttempt);
            setError(
              "Card payments were disabled while this check-in was in progress.",
            );
          } else if (isTerminalPaymentAttemptError(err)) {
            clearAttempt(activeAttempt);
            setError(errorMessage(err, "Failed to check in walk-in."));
          }
        }
      }
      throw err;
    }
  };

  const createQuickDropinSetupIntentSecret = async (
    idempotencyKey: string,
  ): Promise<string> => {
    const playerUserId = selectedCustomer?.userId;
    if (!playerUserId) {
      throw new Error("Select the customer again before saving a card.");
    }
    const target: CardSetupTarget = {
      facilityId,
      playerUserId,
      generation: cardReconciliationGeneration.current,
    };
    cardSetupTargets.current.set(idempotencyKey, target);
    const result = await createQuickDropinPaymentMethodSetupIntent(
      facilityId,
      playerUserId,
      idempotencyKey,
    );
    const currentTarget = cardSetupTargets.current.get(idempotencyKey);
    if (
      currentTarget !== target ||
      cardReconciliationGeneration.current !== target.generation ||
      selectedCustomerIdRef.current !== target.playerUserId
    ) {
      cardSetupTargets.current.delete(idempotencyKey);
      throw new Error("Card setup was canceled.");
    }
    return result.client_secret;
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<PersonAddAlt1Icon />}
        onClick={() => setOpen(true)}
      >
        Quick drop-in
      </Button>
      {open ? (
        <Dialog
          open
          onClose={close}
          fullWidth
          maxWidth="sm"
          aria-labelledby="quick-dropin-title"
        >
          <DialogTitle id="quick-dropin-title" sx={{ pr: 7 }}>
            Quick drop-in
            <IconButton
              aria-label="Close"
              onClick={close}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ maxHeight: "calc(100dvh - 120px)" }}>
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              <DialogContentText>
                Select the customer and their payment path.
              </DialogContentText>
              <CustomerPickerComponent
                facilityId={facilityId}
                facilityName={facilityName}
                value={selectedCustomer}
                disabled={
                  busy ||
                  cardSetupBusy ||
                  attempt !== null ||
                  quickDropinUnavailable
                }
                onChange={(customer) => {
                  cardReconciliationGeneration.current += 1;
                  paymentMethodsRetryGeneration.current += 1;
                  cardSetupTargets.current.clear();
                  setSelectedCustomer(customer);
                  setPaymentOptions(null);
                  setPaymentChoice(null);
                  paymentMethodsRetryingRef.current = false;
                  focusSavedCardAfterRetryRef.current = false;
                  setPaymentMethodsRetrying(false);
                  setPaymentMethodsRetryAnnouncement("");
                  setFocusCardEntryAfterRetry(false);
                  setSelectedPaymentMethodId("");
                  setFrontendConfig(null);
                  setPendingCard(null);
                  setCardSetupError(null);
                  setPaymentError(null);
                  setError(null);
                  setSuccess(null);
                  setPostScaWaiverRefunded(false);
                }}
              />
              {paymentLoading ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  role="status"
                >
                  Loading payment options...
                </Typography>
              ) : null}
              {attempt ? (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2">
                    Retry uses the original {attempt.body.payment_method}{" "}
                    payment and cancellation policy version{" "}
                    {attempt.body.cancellation_policy_version}.
                  </Typography>
                </Paper>
              ) : null}
              {paymentOptions && !attempt ? (
                <FormControl fullWidth>
                  <InputLabel id="payment-method-label">
                    Payment method
                  </InputLabel>
                  <Select
                    labelId="payment-method-label"
                    id="payment-method"
                    label="Payment method"
                    value={paymentChoiceValue(paymentChoice)}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value === "single") {
                        setPaymentChoice({ method: "single" });
                        return;
                      }
                      const [method, subscriptionId] = value.split(":", 2);
                      if (method === "package" || method === "membership") {
                        setPaymentChoice({ method, subscriptionId });
                      }
                    }}
                    disabled={busy || cardSetupBusy || pendingCard !== null}
                  >
                    {(paymentOptions.active_memberships ?? []).map(
                      (membership) => (
                        <MenuItem
                          key={membership.subscription_id}
                          value={`membership:${membership.subscription_id}`}
                        >
                          {membership.option_label}
                        </MenuItem>
                      ),
                    )}
                    {(paymentOptions.active_packages ?? []).map(
                      (packageOption) => (
                        <MenuItem
                          key={packageOption.subscription_id}
                          value={`package:${packageOption.subscription_id}`}
                        >
                          {packageOption.option_label} (
                          {packageOption.remaining_visits} left)
                        </MenuItem>
                      ),
                    )}
                    {paymentOptions.single_visit_available &&
                    paymentOptions.single_price != null ? (
                      <MenuItem value="single">
                        {paymentOptions.single_requires_payment_method
                          ? `Single drop-in - ${formatDropinPrice(
                              paymentOptions.single_price,
                              paymentOptions.currency,
                            )}`
                          : `Free drop-in - ${formatDropinPrice(
                              paymentOptions.single_price,
                              paymentOptions.currency,
                            )}`}
                      </MenuItem>
                    ) : null}
                  </Select>
                </FormControl>
              ) : null}
              {paymentOptions && !attempt ? (
                <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Cancellation policy version{" "}
                    {paymentOptions.checkout_terms.policy_version}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {paymentOptions.checkout_terms.summary}
                  </Typography>
                </Box>
              ) : null}
              {paymentOptions?.single_unavailable_reason ===
                "card_payments_not_ready" && !attempt ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  role="status"
                >
                  Paid drop-in is unavailable until the business finishes
                  payment setup. Memberships and visit passes remain available.
                </Typography>
              ) : null}
              <Box
                component="span"
                aria-live="polite"
                aria-atomic="true"
                sx={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  p: 0,
                  m: -1,
                  overflow: "hidden",
                  clip: "rect(0, 0, 0, 0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }}
              >
                {paymentMethodsRetryAnnouncement}
              </Box>
              {paymentOptions &&
              !attempt &&
              paymentChoice?.method === "single" &&
              paymentOptions.single_requires_payment_method ? (
                paymentMethodsUnavailable || paymentMethodsRetrying ? (
                  <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                    <Alert severity="error">Failed to load saved cards.</Alert>
                    <Button
                      onClick={() => void retryPaymentMethods()}
                      aria-label="Retry saved cards"
                      aria-busy={paymentMethodsRetrying || undefined}
                      aria-disabled={paymentMethodsRetrying || undefined}
                      size="small"
                      type="button"
                      variant="outlined"
                    >
                      {paymentMethodsRetrying
                        ? "Retrying..."
                        : "Retry saved cards"}
                    </Button>
                  </Stack>
                ) : paymentMethodsLoading ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    role="status"
                  >
                    Loading saved cards...
                  </Typography>
                ) : paymentMethods.length > 0 && pendingCard === null ? (
                  <Stack spacing={2}>
                    <Stack spacing={1}>
                      <FormControl fullWidth>
                        <InputLabel id="saved-card-label">Card</InputLabel>
                        <Select
                          labelId="saved-card-label"
                          id="saved-card"
                          label="Card"
                          displayEmpty
                          value={
                            paymentMethods.some(
                              (method) =>
                                method.payment_method_id ===
                                selectedPaymentMethodId,
                            )
                              ? selectedPaymentMethodId
                              : ""
                          }
                          renderValue={(paymentMethodId) => {
                            const method = paymentMethods.find(
                              (candidate) =>
                                candidate.payment_method_id === paymentMethodId,
                            );
                            return method
                              ? `${method.brand.toUpperCase()} ending ${method.last4}`
                              : "Choose card";
                          }}
                          onChange={(event) => {
                            const paymentMethodId = event.target.value;
                            if (
                              paymentMethods.some(
                                (method) =>
                                  method.payment_method_id ===
                                    paymentMethodId && method.is_usable,
                              )
                            ) {
                              setSelectedPaymentMethodId(paymentMethodId);
                            }
                          }}
                          disabled={
                            busy || cardSetupBusy || pendingCard !== null
                          }
                        >
                          {paymentMethods.map((method) => {
                            const unavailableLabel =
                              getSavedPaymentMethodUnavailableLabel(
                                method.unavailable_reason,
                              );
                            return (
                              <MenuItem
                                key={method.payment_method_id}
                                value={method.payment_method_id}
                                disabled={!method.is_usable}
                              >
                                {method.brand.toUpperCase()} ending{" "}
                                {method.last4}
                                {unavailableLabel
                                  ? ` - ${unavailableLabel}`
                                  : null}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      {paymentMethods
                        .filter((method) => !method.is_usable)
                        .map((method) => {
                          const unavailableLabel =
                            getSavedPaymentMethodUnavailableLabel(
                              method.unavailable_reason,
                            );
                          return unavailableLabel ? (
                            <Typography
                              key={method.payment_method_id}
                              variant="caption"
                              color="text.secondary"
                            >
                              {method.brand.toUpperCase()} ending {method.last4}
                              : {unavailableLabel}. Add a new card.
                            </Typography>
                          ) : null;
                        })}
                    </Stack>
                    {needsNewCardEntry ? (
                      <QuickDropinCardEntry
                        autoFocus={focusCardEntryAfterRetry}
                        config={frontendConfig}
                        createSetupIntent={createQuickDropinSetupIntentSecret}
                        error={cardSetupError}
                        loading={cardConfigLoading}
                        onBusyChange={setCardSetupBusy}
                        onCardAdded={handleCardAdded}
                        onRetry={() => {
                          setCardSetupError(null);
                          setCardConfigReload((value) => value + 1);
                        }}
                      />
                    ) : null}
                  </Stack>
                ) : pendingCard ? (
                  <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                    {cardSetupError ? (
                      <Alert severity="error">{cardSetupError}</Alert>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        role="status"
                      >
                        Refreshing the saved card...
                      </Typography>
                    )}
                    <Button
                      disabled={cardSetupBusy || cardSetupError === null}
                      onClick={() => void retryPendingCardRefresh()}
                      size="small"
                      type="button"
                      variant="outlined"
                    >
                      {cardSetupBusy || cardSetupError === null
                        ? "Refreshing..."
                        : "Retry card refresh"}
                    </Button>
                  </Stack>
                ) : paymentMethodsQuery.data !== undefined ? (
                  <QuickDropinCardEntry
                    autoFocus={focusCardEntryAfterRetry}
                    config={frontendConfig}
                    createSetupIntent={createQuickDropinSetupIntentSecret}
                    error={cardSetupError}
                    loading={cardConfigLoading}
                    onBusyChange={setCardSetupBusy}
                    onCardAdded={handleCardAdded}
                    onRetry={() => {
                      setCardSetupError(null);
                      setCardConfigReload((value) => value + 1);
                    }}
                  />
                ) : null
              ) : null}
              {paymentOptions && !paymentOptions.active_now && !attempt ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  role="status"
                >
                  Drop-in is not open right now.
                </Typography>
              ) : null}
              {paymentOptions?.has_active_session && !attempt ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  role="status"
                >
                  This customer is already checked in.
                </Typography>
              ) : null}
              {paymentError ? (
                <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                  <Alert severity="error">{paymentError.message}</Alert>
                  {paymentError.retryable ? (
                    <Button
                      disabled={paymentLoading}
                      onClick={() => {
                        setPaymentError(null);
                        setPaymentOptionsReload((value) => value + 1);
                      }}
                      size="small"
                      type="button"
                      variant="outlined"
                    >
                      Retry payment options
                    </Button>
                  ) : null}
                </Stack>
              ) : null}
              {success ? (
                <Alert severity="success" role="status">
                  ✓ {success}
                </Alert>
              ) : null}
              {error ? <Alert severity="error">{error}</Alert> : null}
              {!quickDropinUnavailable &&
              paymentAuthentication &&
              frontendConfig ? (
                <ConnectedElementsProvider
                  config={frontendConfig}
                  unavailableFallback={
                    <Alert severity="error" role="alert">
                      Secure payment verification is unavailable.
                    </Alert>
                  }
                >
                  <PaymentIntentAuthentication
                    clientSecret={paymentAuthentication.clientSecret}
                    onAuthenticated={finishPaymentAuthentication}
                    render={(model) => (
                      <Paper variant="outlined" sx={{ p: 1.5 }} role="status">
                        <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                          {model.error ? (
                            <Alert severity="error">{model.message}</Alert>
                          ) : (
                            <Typography variant="body2">
                              {model.message}
                            </Typography>
                          )}
                          <Button
                            type="button"
                            size="small"
                            variant="contained"
                            onClick={model.authenticate}
                            disabled={model.actionDisabled}
                            aria-busy={model.busy || undefined}
                          >
                            {model.actionLabel}
                          </Button>
                        </Stack>
                      </Paper>
                    )}
                  />
                </ConnectedElementsProvider>
              ) : null}
            </Stack>
          </DialogContent>
          {paymentAuthentication || quickDropinUnavailable ? null : (
            <DialogActions>
              <Button
                size="small"
                variant="contained"
                onClick={() => void handleCheckIn()}
                disabled={
                  busy ||
                  cardSetupBusy ||
                  pendingCard !== null ||
                  !operatorUid ||
                  requestBody === null
                }
                sx={{ "&.Mui-disabled": { pointerEvents: "auto" } }}
              >
                {busy
                  ? "Checking in..."
                  : postScaWaiverRefunded &&
                      requestBody?.payment_method === "single"
                    ? "Start new payment"
                    : attempt
                      ? "Retry check-in"
                      : "Check in customer"}
              </Button>
            </DialogActions>
          )}
        </Dialog>
      ) : null}
    </>
  );
}
